import Header from "../../../components/layout/Header/Header";
import Footer from "../../../components/layout/Footer/Footer";
import Container from "../../../components/layout/Container/Container";
import s from "./HomePage.module.scss";

import ServicesByDirections from "../../../components/sections/ServicesByDirections/ServicesByDirections";
import Technologies from "../../../components/sections/Technologies/Technologies";
import VideoTeaser from "../../../components/sections/VideoTeaser/VideoTeaser";
import Feedback from "components/sections/Feedback/Feedback";

import { useClinicInfo } from "../../../api/queries/useClinicInfo";
import { useEmployees } from "../../../api/queries/useEmployees";
import { useGallery } from "../../../api/queries/useGallery";
import { toMap } from "../../../app/clinicInfoMap";

import type { components } from "../../../api/schema";

type Employee = components["schemas"]["Employee"];
type Gallery = components["schemas"]["Gallery"];

const HOME_HERO_EMPLOYEE_ID_KEY = "home.hero.employeeId";
const HOME_HERO_AVATAR_ID_KEY = "home.hero.avatarId";

function safeImgSrc(path?: string) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function getAvatarId(e?: Employee | null): number | undefined {
  if (!e) return undefined;

  const anyE = e as any;
  const v = anyE.avatar_path_id ?? anyE.image_path_id;

  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function buildHeroName(e?: Employee | null) {
  if (!e) return "Стоматология";

  const surname = e.surname?.trim() || "";
  const namePart = [e.name, (e as any).patronymic].filter(Boolean).join(" ").trim();

  if (surname && namePart) return `${surname}\n${namePart}`;
  return [surname, namePart].filter(Boolean).join(" ").trim() || "Стоматология";
}

function splitLines(text?: string, limit = 4) {
  return (text || "")
    .split(/\n|•|;/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, limit);
}

function EmployeeHeroPhoto({
  employee,
  galleryMap,
  overrideAvatarId,
}: {
  employee?: Employee | null;
  galleryMap: Map<number, Gallery>;
  overrideAvatarId?: number;
}) {
  if (overrideAvatarId && galleryMap.has(overrideAvatarId)){
    const g = galleryMap.get(overrideAvatarId)!;
    const src = safeImgSrc(g.image_path);
    return(
      <img
	className={s.heroPhotoImg}	
	src={src}
	alt={buildHeroName(employee)}
	loading="eager"
      />
    )
  }
  const photoId = getAvatarId(employee);
  const g = photoId ? galleryMap.get(photoId) : undefined;
  const src = safeImgSrc(g?.image_path);

  if (!src) {
    return <div className={s.heroPhotoFallback}>Фото сотрудника</div>;
  }

  return <img className={s.heroPhotoImg} src={src} alt={buildHeroName(employee)} loading="eager" />;
}

export default function HomePage() {
  const { data: clinicInfo } = useClinicInfo();
  const { data: employees } = useEmployees();
  const { data: gallery } = useGallery();

  const cfg = toMap(clinicInfo ?? []);

  const galleryMap = new Map<number, Gallery>();
  (gallery ?? []).forEach((g: Gallery) => {
    if (typeof g.id === "number") galleryMap.set(g.id, g);
  });

  const selectedEmployeeId = Number(cfg[HOME_HERO_EMPLOYEE_ID_KEY] || "");
  const selectedAvatarId = Number(cfg[HOME_HERO_AVATAR_ID_KEY] || "");
  const selectedEmployee =
    Number.isFinite(selectedEmployeeId) && selectedEmployeeId > 0
      ? (employees ?? []).find((e) => (e as any).id === selectedEmployeeId) ?? null
      : null;

  const heroName = buildHeroName(selectedEmployee);
  const heroPosition = ((selectedEmployee as any)?.position_at_work || "").trim();
  const heroRegalia = splitLines(((selectedEmployee as any)?.description || "").trim(), 5);

  return (
    <div className={s.page}>
      <Header />

      <main className={s.main}>
        <section className={`${s.hero} section section--loose`}>
          <Container>
            {selectedEmployee ? (
              <div className={s.heroCard}>
                <div className={s.heroGrid}>
                  <div className={s.heroText}>
                    <h1 className={s.heroName}>{heroName}</h1>

                    {heroPosition ? <p className={s.heroRole}>{heroPosition}</p> : null}

                    <div className={s.actions}>
                      <a href="https://dikidi.net/1957964?p=3.pi-po-sm-mi-ssm&o=1&m=4210192" className={s.primary}>
                        Записаться на прием
                      </a>
                    </div>

                   {/*  {heroRegalia.length > 0 ? (
                      <ul className={s.heroFacts}>
                        {heroRegalia.map((item) => (
                          <li key={item} className={s.heroFactItem}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : null} */}
                 </div>

                  <div className={s.heroPhoto}>
                    <EmployeeHeroPhoto employee={selectedEmployee} galleryMap={galleryMap} overrideAvatarId={selectedAvatarId} />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h1 className={s.h1}>Стоматология</h1>
                <p className={s.lead}>Мы победили кариес!</p>
              </>
            )}
          </Container>
        </section>
	
	<section className={`section section--tight ${s.sectionCompact}`}>
	   <VideoTeaser />
	</section>

        <section className={`section section--tight ${s.sectionCompact}`}>
          <Technologies />
        </section>

        <section id="feedback" className={`section section--tight ${s.sectionCompact}`}>
          <Feedback />
        </section>
      </main>

      <Footer />
    </div>
  );
}
